/**
 * Registers the writing board's UI intents. Idempotent — called once at app startup.
 *
 * - `writing.proposeBrief`: the evaluator's propose_brief tool emits it with the
 *   card it evaluates and the brief (server: `propose_brief_tool.py`); the
 *   handler records it so the card's BriefPanel can offer Save / Discard.
 */
import { registerIntent } from '@features/agentUi/uiIntentDispatcher';

import { useBriefProposalStore } from './stores/useBriefProposalStore';

import type { Brief, NextMove } from './types';

const NEXT_MOVES: readonly NextMove[] = ['slate', 'keep_as_material', 'delete', 'stay_in_ideas'];

let registered = false;

/** A string, or null for anything else (blank included). */
function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/** The brief in an intent's args, or null when it is not one (fail closed). */
export function briefFromArgs(value: unknown): Brief | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const verdict = text(raw.verdict);
  const nextMove = raw.next_move as NextMove;
  if (!verdict || !NEXT_MOVES.includes(nextMove)) return null;
  const items = Array.isArray(raw.open_items) ? raw.open_items : [];
  return {
    verdict,
    next_move: nextMove,
    material_kind: text(raw.material_kind) as Brief['material_kind'],
    form: text(raw.form),
    center: text(raw.center),
    size: text(raw.size),
    open_items: items.filter((i): i is string => typeof i === 'string' && !!i.trim()),
    long_output: text(raw.long_output),
    conversation_id: text(raw.conversation_id),
  };
}

/** Register the writing board's intent handlers once. */
export function registerWritingBoardIntents(): void {
  if (registered) return;
  registered = true;

  registerIntent('writing.proposeBrief', args => {
    const cardId = text(args.card_id);
    const brief = briefFromArgs(args.brief);
    if (!cardId || !brief) return;
    useBriefProposalStore.getState().propose(cardId, brief);
  });
}

/** Test-only: allow re-registration after a dispatcher reset. */
export function __resetWritingBoardIntentRegistrationForTests(): void {
  registered = false;
}
